import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Loader2, Send } from "lucide-react";
import { LeadFeedback } from "@/components/LeadFeedback";
import { postLead } from "@/lib/api";
import { CONTACT } from "@/lib/contact";

const DETAILS = [
  { icon: MapPin, label: "Office", value: "Sector 62, Noida, Uttar Pradesh 201301", href: "https://maps.google.com/?q=Sector+62+Noida" },
  { icon: Phone, label: "Call us", value: CONTACT.phoneDisplay, href: CONTACT.phoneHref },
  { icon: Mail, label: "Email", value: CONTACT.email, href: CONTACT.emailHref },
  { icon: Clock, label: "Hours", value: "Mon – Sat · 10:00 – 18:30" },
];

const inputCls =
  "w-full rounded-2xl border border-white/10 bg-ink px-4 py-3.5 text-sm text-frost placeholder:text-mist/60 outline-none transition-all duration-300 focus:border-mint/60 focus:ring-2 focus:ring-mint/20";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setFeedback(null);
    try {
      await postLead("/enquiries", form);
      setFeedback({ type: "success", message: "Thank you! Your enquiry has been received and saved." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setFeedback({ type: "error", message: err.message || "Could not send. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="container-x py-20 lg:py-32">
      <div className="grid grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="overline-label flex items-center gap-3">
            <span className="h-px w-8 bg-mint" />
            Visit us
          </p>
          <h2 data-testid="contact-heading" className="mt-5 font-display text-3xl font-light leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl">
            Come say <span className="italic text-mint">hello.</span>
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-mist">
            Walk in with your marksheet, your questions and your parents — we'll put the kettle on and map the road ahead.
          </p>
          <div className="mt-10 space-y-5">
            {DETAILS.map((d, i) => (
              <motion.div
                key={d.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center gap-4"
                data-testid={`contact-detail-${i}`}
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-mint/30 bg-surface text-mint">
                  <d.icon className="h-5 w-5" strokeWidth={1.7} />
                </span>
                <div className="min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">{d.label}</p>
                  {d.href ? (
                    <a
                      href={d.href}
                      target={d.href.startsWith("http") ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      className="mt-0.5 block break-words text-[15px] font-semibold text-frost transition-colors hover:text-mint [overflow-wrap:anywhere]"
                      data-testid={`contact-detail-link-${i}`}
                    >
                      {d.value}
                    </a>
                  ) : (
                    <p className="mt-0.5 text-[15px] font-semibold text-frost">{d.value}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-[28px] border border-white/10 bg-surface p-7 sm:p-9"
        >
          <h3 className="font-display text-2xl tracking-tight">Drop a question</h3>
          <p className="mt-2 text-sm text-mist">Counselors reply between sessions — usually within a day.</p>
          <form onSubmit={submit} data-testid="contact-enquiry-form" className="mt-7 space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input required maxLength={120} autoComplete="name" value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="contact-name-input" aria-label="Your name" />
              <input required type="email" autoComplete="email" value={form.email} onChange={set("email")} placeholder="Email" className={inputCls} data-testid="contact-email-input" aria-label="Email" />
            </div>
            <input type="tel" maxLength={30} autoComplete="tel" value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" className={inputCls} data-testid="contact-phone-input" aria-label="Phone" />
            <textarea required rows={4} maxLength={2000} value={form.message} onChange={set("message")} placeholder="How can we help?" className={`${inputCls} resize-none`} data-testid="contact-message-input" aria-label="Message" />
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-form-submit-btn"
              className="group flex w-full items-center justify-center gap-2 rounded-full bg-mint py-4 text-sm font-bold text-ink transition-all duration-300 hover:bg-mintlight hover:shadow-[0_0_44px_rgba(74,222,128,0.35)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
              ) : (
                <>Send message <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" /></>
              )}
            </button>
            <LeadFeedback feedback={feedback} form="contact" />
          </form>
        </motion.div>
      </div>
    </section>
  );
}
